import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError, isAxiosError } from 'axios'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import { useOrgIntegrations } from '@/app/entities/integrations/model'
import { useCurrentOrg } from '@/app/entities/org/model'
import {
    createProjectProductsOzonApiV1ProductsOzonProductsIntegrationIdPost,
    createProjectProductsWildberriesApiV1ProductsWildberriesProductsIntegrationIdPost,
    getCeleryTaskStatusApiV1CeleryTaskTaskIdGet,
    getProductsListApiV1ProductsOzonListIntegrationIdGet,
    getWbProductsApiV1ProductsWildberriesListIntegrationIdGet,
} from '@/common/api/client/sdk.gen'
import type {
    BodyCreateProjectProductsWildberriesApiV1ProductsWildberriesProductsIntegrationIdPost,
    CeleryTaskDetailSchema,
    CreateProductResponseShema,
    GetProductsListApiV1ProductsOzonListIntegrationIdGetData,
    GetWbProductsApiV1ProductsWildberriesListIntegrationIdGetData,
} from '@/common/api/client/types.gen'

type OzonListQuery = NonNullable<GetProductsListApiV1ProductsOzonListIntegrationIdGetData['query']>
type WbListQuery = NonNullable<GetWbProductsApiV1ProductsWildberriesListIntegrationIdGetData['query']>

type ImportProductsListErrorBody = {
    detail?: string | Array<{ msg?: string }>
}

type ImportMutationErrorBody = {
    detail?: string | unknown
}

const getImportMutationErrorMessage = (error: AxiosError<ImportMutationErrorBody>): string => {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) {
        return detail.trim()
    }
    return error.message?.trim() || 'Произошла ошибка'
}

export const getImportProductsListErrorMessage = (error: unknown): string | null => {
    if (!isAxiosError<ImportProductsListErrorBody>(error)) {
        return null
    }
    const detail = error.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) {
        return detail.trim()
    }
    if (Array.isArray(detail)) {
        const parts = detail.map(item => (item && typeof item.msg === 'string' ? item.msg.trim() : '')).filter(Boolean)
        if (parts.length > 0) {
            return parts.join('. ')
        }
    }
    const fallback = error.message?.trim()
    return fallback || null
}

/** Не дублировать запросы списка при 4xx: глобальный retry иначе даёт до 3 повторов. */
const retryImportProductsList = (failureCount: number, error: unknown): boolean => {
    const status = isAxiosError(error) ? error.response?.status : undefined
    if (status != null && status >= 400 && status < 500) {
        return false
    }
    return failureCount < 3
}

const ozonGetProducts = async (integrationId: string, params: OzonListQuery) => {
    const res = await getProductsListApiV1ProductsOzonListIntegrationIdGet({
        path: { integration_id: integrationId },
        query: params,
    })
    return res.data!
}

const wildberriesGetProducts = async (integrationId: string, params: WbListQuery) => {
    const res = await getWbProductsApiV1ProductsWildberriesListIntegrationIdGet({
        path: { integration_id: integrationId },
        query: params,
    })
    return res.data!
}

const createPixorionOzonProduct = async (integrationId: string, data: Array<number>) => {
    const res = await createProjectProductsOzonApiV1ProductsOzonProductsIntegrationIdPost({
        path: { integration_id: integrationId },
        body: data,
    })
    return res.data!
}

const createPixorionWildberriesProduct = async (
    integrationId: string,
    data: BodyCreateProjectProductsWildberriesApiV1ProductsWildberriesProductsIntegrationIdPost
) => {
    const res = await createProjectProductsWildberriesApiV1ProductsWildberriesProductsIntegrationIdPost({
        path: { integration_id: integrationId },
        body: data,
    })
    return res.data!
}

const getCeleryTask = async (taskId: string) => {
    const res = await getCeleryTaskStatusApiV1CeleryTaskTaskIdGet({ path: { task_id: taskId } })
    return res.data!
}

export interface ImportFormData {
    selectedProductIds: string[]
}

export const useImportTable = () => {
    const [selectedMarketplace, setSelectedMarketplace] = useState<'ozon' | 'wildberries'>('ozon')
    const [taskId, setTaskId] = useState<string | null>(null)
    const [pollingResult, setPollingResult] = useState<CeleryTaskDetailSchema | null>(null)
    const [shouldPoll, setShouldPoll] = useState(false)
    const { data: currentOrg } = useCurrentOrg()
    const { data: integrations, isLoading: integrationsLoading } = useOrgIntegrations(currentOrg?.id)
    const integrationsReady = Boolean(currentOrg?.id) && !integrationsLoading
    const ozonIntegration = integrations?.find(integration => integration.marketplace === 'ozon')
    const wildberriesIntegration = integrations?.find(integration => integration.marketplace === 'wildberries')
    const queryClient = useQueryClient()
    const toastContext = useOptionalToast()
    const celeryTerminalToastMarkerRef = useRef<string | null>(null)

    const handleImportMutationError = useCallback(
        (error: AxiosError<ImportMutationErrorBody>) => {
            if (!toastContext) {
                return
            }
            toastContext.showToast({ type: 'error', message: getImportMutationErrorMessage(error) })
        },
        [toastContext]
    )

    const createPixorionOzonProductMutation = useMutation<
        CreateProductResponseShema,
        AxiosError<ImportMutationErrorBody>,
        { data: Array<number>; integrationId: string }
    >({
        mutationFn: ({ data, integrationId }) => createPixorionOzonProduct(integrationId, data),
        onError: handleImportMutationError,
    })

    const createPixorionWildberriesProductMutation = useMutation<
        CreateProductResponseShema,
        AxiosError<ImportMutationErrorBody>,
        {
            data: BodyCreateProjectProductsWildberriesApiV1ProductsWildberriesProductsIntegrationIdPost
            integrationId: string
        }
    >({
        mutationFn: ({ data, integrationId }) => createPixorionWildberriesProduct(integrationId, data),
        onError: handleImportMutationError,
    })

    const form = useForm<ImportFormData>({
        defaultValues: {
            selectedProductIds: [],
        },
    })

    type OzonPageParam = { last_id: string } | undefined

    const ozonProductsInfinite = useInfiniteQuery({
        queryKey: ['ozonProductsInfinite', ozonIntegration?.id],
        initialPageParam: undefined as OzonPageParam,
        queryFn: ({ pageParam }) => {
            const params: OzonListQuery = { limit: 50 }
            if (pageParam?.last_id) {
                params.last_id = pageParam.last_id
            }
            return ozonGetProducts(ozonIntegration!.id, params)
        },
        getNextPageParam: lastPage => {
            const np = lastPage.next_page as { last_id?: string } | undefined
            return np?.last_id ? { last_id: np.last_id } : undefined
        },
        enabled: selectedMarketplace === 'ozon' && !!ozonIntegration?.id,
        retry: retryImportProductsList,
    })

    type WbPageParam =
        | {
              cursor_nm_id: number
              cursor_updated_at?: string
          }
        | undefined

    const wildberriesProductsInfinite = useInfiniteQuery({
        queryKey: ['wildberriesProductsInfinite', wildberriesIntegration?.id],
        initialPageParam: undefined as WbPageParam,
        queryFn: ({ pageParam }) => {
            const params: WbListQuery = { limit: 50 }
            if (pageParam?.cursor_nm_id != null) {
                params.cursor_nm_id = pageParam.cursor_nm_id
            }
            if (pageParam?.cursor_updated_at) {
                params.cursor_updated_at = pageParam.cursor_updated_at
            }
            return wildberriesGetProducts(wildberriesIntegration!.id, params)
        },
        getNextPageParam: lastPage => {
            const np = lastPage.next_page
            if (np == null || np.cursor_nm_id == null) return undefined
            return {
                cursor_nm_id: np.cursor_nm_id as number,
                ...(np.cursor_updated_at ? { cursor_updated_at: np.cursor_updated_at as string } : {}),
            }
        },
        enabled: selectedMarketplace === 'wildberries' && !!wildberriesIntegration?.id,
        retry: retryImportProductsList,
    })

    const flatProducts = useMemo(() => {
        const data = selectedMarketplace === 'ozon' ? ozonProductsInfinite.data : wildberriesProductsInfinite.data
        return data?.pages.flatMap(page => page.products ?? []) ?? []
    }, [selectedMarketplace, ozonProductsInfinite.data, wildberriesProductsInfinite.data])

    const activeInfinite = selectedMarketplace === 'ozon' ? ozonProductsInfinite : wildberriesProductsInfinite

    const listFetchErrorMessage = useMemo(
        () => getImportProductsListErrorMessage(activeInfinite.error),
        [activeInfinite.error]
    )

    const fetchNextPage = activeInfinite.fetchNextPage
    const hasNextPage = activeInfinite.hasNextPage
    const isFetchingNextPage = activeInfinite.isFetchingNextPage

    const handleImportTableVisibleIndicesChange = useCallback(
        (indices: number[], scrollIndex: number) => {
            const len = flatProducts.length
            if (len === 0) return
            const maxVisible = indices.length > 0 ? Math.max(...indices) : scrollIndex
            const nearEndThreshold = 12
            if (maxVisible >= len - nearEndThreshold && hasNextPage && !isFetchingNextPage) {
                void fetchNextPage()
            }
        },
        [flatProducts.length, hasNextPage, isFetchingNextPage, fetchNextPage]
    )

    const celeryTaskQuery = useQuery({
        queryKey: ['celeryTask', taskId],
        queryFn: () => getCeleryTask(taskId!),
        enabled: !!taskId && shouldPoll,
        refetchInterval: shouldPoll ? 2000 : false,
        refetchIntervalInBackground: true,
    })

    // Обработка успешного выполнения задачи
    useEffect(() => {
        if (
            celeryTaskQuery.data &&
            (celeryTaskQuery.data.state === 'SUCCESS' || celeryTaskQuery.data.state === 'FAILURE')
        ) {
            queryClient.invalidateQueries({ queryKey: ['org_integrations', currentOrg?.id] })
            queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
            queryClient.invalidateQueries({ queryKey: ['ozonProductsInfinite'] })
            queryClient.invalidateQueries({ queryKey: ['wildberriesProductsInfinite'] })
        }
    }, [celeryTaskQuery.data, queryClient, currentOrg?.id])

    const toggleProductSelection = (productId: string) => {
        const product = flatProducts.find(p => p.marketplace_id?.toString() === productId)

        if (product?.is_exists) {
            return
        }

        const currentIds = form.getValues('selectedProductIds')
        const updatedIds = currentIds.includes(productId)
            ? currentIds.filter(id => id !== productId)
            : [...currentIds, productId]
        form.setValue('selectedProductIds', updatedIds)
    }

    const handleSubmit = useCallback(
        (data: ImportFormData) => {
            if (createPixorionOzonProductMutation.isPending) {
                return
            }

            if (selectedMarketplace === 'ozon' && ozonIntegration?.id && data.selectedProductIds.length > 0) {
                const productIds = data.selectedProductIds.map(id => parseInt(id, 10))
                createPixorionOzonProductMutation.mutate({
                    data: productIds,
                    integrationId: ozonIntegration.id,
                })
            }
            if (
                selectedMarketplace === 'wildberries' &&
                wildberriesIntegration?.id &&
                data.selectedProductIds.length > 0
            ) {
                const productsData = data.selectedProductIds.reduce(
                    (acc, id) => {
                        const found = flatProducts.find(product => product.marketplace_id == id)
                        if (found) {
                            acc.nm_ids.push(found.marketplace_id as number)
                            acc.imt_ids.push(found.imt_id as number)
                        }
                        return acc
                    },
                    {
                        nm_ids: [] as number[],
                        imt_ids: [] as number[],
                    }
                )

                createPixorionWildberriesProductMutation.mutate({
                    data: productsData,
                    integrationId: wildberriesIntegration.id,
                })
            } else {
                console.log('Selected product IDs:', data.selectedProductIds)
            }
        },
        [
            selectedMarketplace,
            ozonIntegration?.id,
            createPixorionOzonProductMutation,
            wildberriesIntegration?.id,
            createPixorionWildberriesProductMutation,
            flatProducts,
        ]
    )

    useEffect(() => {
        form.setValue('selectedProductIds', [])
        setTaskId(null)
        setPollingResult(null)
        setShouldPoll(false)
    }, [selectedMarketplace, form])

    useEffect(() => {
        if (createPixorionOzonProductMutation.isSuccess) {
            const response = createPixorionOzonProductMutation.data
            if (response?.task_id) {
                setTaskId(response.task_id)
                setShouldPoll(true)
            }
            form.setValue('selectedProductIds', [])
            if (selectedMarketplace === 'ozon') {
                void ozonProductsInfinite.refetch()
            }
            createPixorionOzonProductMutation.reset()
        }
    }, [
        createPixorionOzonProductMutation.isSuccess,
        createPixorionOzonProductMutation.data,
        form,
        selectedMarketplace,
        ozonProductsInfinite,
        createPixorionOzonProductMutation,
    ])

    useEffect(() => {
        if (createPixorionWildberriesProductMutation.isSuccess) {
            const response = createPixorionWildberriesProductMutation.data
            if (response?.task_id) {
                setTaskId(response.task_id)
                setShouldPoll(true)
            }
            form.setValue('selectedProductIds', [])
            if (selectedMarketplace === 'wildberries') {
                void wildberriesProductsInfinite.refetch()
            }
            createPixorionWildberriesProductMutation.reset()
        }
    }, [
        createPixorionWildberriesProductMutation.isSuccess,
        createPixorionWildberriesProductMutation.data,
        form,
        selectedMarketplace,
        wildberriesProductsInfinite,
        createPixorionWildberriesProductMutation,
    ])

    useEffect(() => {
        if (celeryTaskQuery.data) {
            setPollingResult(celeryTaskQuery.data)

            if (celeryTaskQuery.data.state === 'SUCCESS' || celeryTaskQuery.data.state === 'FAILURE') {
                setShouldPoll(false)
            }
        }
    }, [celeryTaskQuery.data])

    useEffect(() => {
        if (!taskId) {
            celeryTerminalToastMarkerRef.current = null
        }
    }, [taskId])

    useEffect(() => {
        if (!toastContext || !taskId || !celeryTaskQuery.data) {
            return
        }
        const state = celeryTaskQuery.data.state
        if (state !== 'SUCCESS' && state !== 'FAILURE') {
            return
        }
        const marker = `${taskId}:${state}`
        if (celeryTerminalToastMarkerRef.current === marker) {
            return
        }
        celeryTerminalToastMarkerRef.current = marker
        if (state === 'SUCCESS') {
            toastContext.showToast({ type: 'success', message: 'Товары успешно импортированы!' })
            return
        }
        const traceback = celeryTaskQuery.data.traceback?.trim()
        toastContext.showToast({
            type: 'error',
            message: traceback && traceback.length > 0 ? traceback : 'Ошибка при импорте',
        })
    }, [toastContext, taskId, celeryTaskQuery.data])

    const onSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault()
            form.handleSubmit(handleSubmit)(e)
        },
        [form, handleSubmit]
    )

    const resetImportState = useCallback(() => {
        setTaskId(null)
        setPollingResult(null)
        setShouldPoll(false)
        form.setValue('selectedProductIds', [])
    }, [form])

    return {
        selectedMarketplace,
        setSelectedMarketplace,
        integrations,
        integrationsReady,
        importTable: integrations,
        isLoading:
            (selectedMarketplace === 'ozon' &&
                !!ozonIntegration?.id &&
                (ozonProductsInfinite.isPending || ozonProductsInfinite.isLoading)) ||
            (selectedMarketplace === 'wildberries' &&
                !!wildberriesIntegration?.id &&
                (wildberriesProductsInfinite.isPending || wildberriesProductsInfinite.isLoading)),
        flatProducts,
        listFetchErrorMessage,
        form,
        toggleProductSelection,
        handleSubmit: onSubmit,
        selectedProductIds: form.watch('selectedProductIds'),
        isSubmitting: createPixorionOzonProductMutation.isPending || createPixorionWildberriesProductMutation.isPending,
        submitError: createPixorionOzonProductMutation.error || createPixorionWildberriesProductMutation.error,
        isSubmitSuccess:
            createPixorionOzonProductMutation.isSuccess || createPixorionWildberriesProductMutation.isSuccess,
        onVisibleIndicesChange: handleImportTableVisibleIndicesChange,
        isFetchingNextPage: activeInfinite.isFetchingNextPage,

        taskId,
        pollingResult,
        isPolling: celeryTaskQuery.isLoading,
        pollingError: celeryTaskQuery.error,
        resetImportState,
    }
}
